<template>
  <v-container fluid class="mesa-dashboard pa-0">
    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
    <h2 class="subheading grey--text">Mesa Directiva</h2>
    <p class="grey--text text--darken-1 mb-4">Resumen ejecutivo — {{ todayLabelLong }}</p>

    <v-row>
      <v-col cols="12" sm="6" md="4" lg="2" v-for="card in summaryCards" :key="card.label">
        <v-card class="elevation-2 stat-card">
          <v-card-text>
            <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-label grey--text">{{ card.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12" md="7">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon color="orange darken-1" left>mdi-alert-circle-outline</v-icon>
            Pendientes de la Mesa Directiva
            <v-spacer></v-spacer>
          </v-card-title>
          <v-list two-line>
            <v-list-item v-for="(item, i) in pendientes" :key="i">
              <v-list-item-icon>
                <v-icon :color="item.color">{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="caption font-weight-bold text-uppercase" :style="{ color: item.colorHex }">{{ item.tipo }}</v-list-item-subtitle>
                <v-list-item-title class="text-wrap">{{ item.descripcion }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>{{ item.fecha }}
                  <v-chip x-small class="ml-2" :color="prioridadColor(item.prioridad)" text-color="white">{{ item.prioridad }}</v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn text color="primary" small :to="item.to">{{ item.accion }}</v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon color="primary" left>mdi-calendar-clock</v-icon>
            Próxima reunión
          </v-card-title>
          <v-card-text v-if="proximaReunion">
            <div class="title font-weight-bold mb-2">{{ proximaReunion.titulo }}</div>
            <div class="d-flex align-center mb-1">
              <v-icon small color="grey" class="mr-2">mdi-calendar</v-icon>
              {{ proximaReunion.fecha }} · {{ proximaReunion.hora }}
            </div>
            <div class="d-flex align-center mb-1">
              <v-icon small color="grey" class="mr-2">{{ String(proximaReunion.modalidad).toLowerCase() === 'virtual' ? 'mdi-video' : 'mdi-map-marker' }}</v-icon>
              {{ proximaReunion.lugar }} · {{ proximaReunion.modalidad }}
            </div>
            <div class="d-flex align-center mb-3">
              <v-icon small color="grey" class="mr-2">mdi-account-multiple</v-icon>
              {{ proximaReunion.asistentesConfirmados }} de {{ proximaReunion.totalParticipantes }} integrantes confirmados
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="overline grey--text mb-2">Agenda</div>
            <ol class="agenda-list">
              <li v-for="(tema, i) in proximaReunion.agenda" :key="i">{{ tema }}</li>
            </ol>
            <v-btn color="primary" class="mt-3" :to="`/mesa_directiva/reuniones/${proximaReunion.id}`">
              Ver reunión
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-text>
          <v-card-text v-else>
            <v-alert type="info" text dense>No hay reuniones próximas programadas.</v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import mesaDirectivaService from '@/services/mesaDirectivaService'
import acuerdosService from '@/services/acuerdosService'
import votacionesService from '@/services/votacionesService'
import solicitudesService from '@/services/solicitudesService'
import { prioridadColor } from './mesaDirectivaMockData'

const PAGE_SIZE = 100

export default {
  name: 'MesaDirectivaDashboard',
  data () {
    return {
      loading: false,
      miembros: [],
      reuniones: [],
      acuerdos: [],
      votaciones: [],
      solicitudes: []
    }
  },
  created () {
    this.loadData()
  },
  computed: {
    todayLabelLong () {
      return new Date().toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
    },
    proximaReunion () {
      return this.reunionesPendientes
        .slice()
        .sort((a, b) => String(a.fechaRaw).localeCompare(String(b.fechaRaw)))[0]
    },
    acuerdosPendientes () {
      return this.acuerdos.filter(a => a.estado === 'Pendiente' || a.estado === 'En proceso')
    },
    votacionesAbiertas () {
      return this.votaciones.filter(v => v.estado === 'Abierta')
    },
    solicitudesPendientes () {
      return this.solicitudes.filter(s => s.estado === 'Pendiente' || s.estado === 'En revisión')
    },
    reunionesPendientes () {
      return this.reuniones.filter(r => r.estado === 'Próxima')
    },
    summaryCards () {
      return [
        { label: 'Integrantes', value: this.miembros.length, icon: 'mdi-account-group-outline', color: 'primary' },
        { label: 'Próxima reunión', value: this.proximaReunion ? this.proximaReunion.fecha : '—', icon: 'mdi-calendar-clock', color: 'blue' },
        { label: 'Reuniones pendientes', value: this.reunionesPendientes.length, icon: 'mdi-calendar-multiple', color: 'grey darken-1' },
        { label: 'Acuerdos pendientes', value: this.acuerdosPendientes.length, icon: 'mdi-clipboard-check-outline', color: 'orange darken-1' },
        { label: 'Votaciones abiertas', value: this.votacionesAbiertas.length, icon: 'mdi-vote-outline', color: 'blue' },
        { label: 'Solicitudes pendientes', value: this.solicitudesPendientes.length, icon: 'mdi-inbox-outline', color: 'red' }
      ]
    },
    pendientes () {
      const items = []
      this.solicitudesPendientes.forEach(s => items.push({
        tipo: 'Solicitud',
        icon: 'mdi-inbox-outline',
        color: 'blue',
        colorHex: '#3F51B5',
        descripcion: s.descripcion,
        fecha: s.fecha,
        prioridad: s.prioridad,
        accion: 'Revisar',
        to: '/mesa_directiva/solicitudes'
      }))
      this.acuerdosPendientes.forEach(a => items.push({
        tipo: 'Acuerdo',
        icon: 'mdi-clipboard-check-outline',
        color: 'orange darken-1',
        colorHex: '#FB8C00',
        descripcion: a.titulo,
        fecha: 'Límite: ' + a.fechaLimite,
        prioridad: a.estado === 'En proceso' ? 'Media' : 'Alta',
        accion: 'Ver acuerdo',
        to: '/mesa_directiva/acuerdos'
      }))
      this.votacionesAbiertas.forEach(v => items.push({
        tipo: 'Votación',
        icon: 'mdi-vote-outline',
        color: 'blue',
        colorHex: '#3F51B5',
        descripcion: v.tema,
        fecha: 'Cierra: ' + v.fechaCierre,
        prioridad: 'Alta',
        accion: 'Votar',
        to: '/mesa_directiva/votaciones'
      }))
      this.reunionesPendientes.forEach(r => items.push({
        tipo: 'Reunión',
        icon: 'mdi-calendar-clock',
        color: 'grey darken-1',
        colorHex: '#616161',
        descripcion: r.titulo + ' — confirmar asistencia',
        fecha: r.fecha,
        prioridad: 'Media',
        accion: 'Ver reunión',
        to: `/mesa_directiva/reuniones/${r.id}`
      }))
      return items
    }
  },
  methods: {
    prioridadColor,
    async loadData () {
      this.loading = true
      const results = await Promise.allSettled([
        mesaDirectivaService.getMembers(),
        mesaDirectivaService.getMeetings(),
        acuerdosService.getAcuerdos(1, PAGE_SIZE),
        votacionesService.getVotaciones(1, PAGE_SIZE),
        solicitudesService.getSolicitudes(1, PAGE_SIZE)
      ])
      const [miembros, reuniones, acuerdos, votaciones, solicitudes] = results
      if (miembros.status === 'fulfilled') this.miembros = miembros.value.filter(m => m.status === 'active')
      if (reuniones.status === 'fulfilled') this.reuniones = reuniones.value.map(this.mapearReunion)
      if (acuerdos.status === 'fulfilled') this.acuerdos = acuerdos.value
      if (votaciones.status === 'fulfilled') this.votaciones = votaciones.value
      if (solicitudes.status === 'fulfilled') this.solicitudes = solicitudes.value

      const failed = results.find(r => r.status === 'rejected')
      if (failed) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al cargar el resumen: ${failed.reason && failed.reason.message}`)
        this.$store.commit('snackbar_activate_true')
      }
      this.loading = false
    },
    mapearReunion (r) {
      const estados = { upcoming: 'Próxima', finalized: 'Finalizada', cancelled: 'Cancelada' }
      return {
        id: r.id,
        titulo: r.title,
        fecha: this.formatearFecha(r.date),
        fechaRaw: r.date,
        hora: r.time,
        lugar: r.location,
        modalidad: r.modality,
        estado: estados[r.status] || r.status,
        asistentesConfirmados: r.confirmed_attendees,
        totalParticipantes: r.total_participants,
        agenda: r.agenda || []
      }
    },
    formatearFecha (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.mesa-dashboard {
  height: 100%;
  overflow-y: auto;
}

.stat-card {
  text-align: center;
  height: 100%;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.85rem;
}

.agenda-list {
  padding-left: 20px;
  margin: 0;
  color: #616161;
  font-size: 0.875rem;
}

.agenda-list li {
  margin-bottom: 4px;
}
</style>
